/* eslint no-console: 0 */
import axios from 'axios';
import { getServiceAndBaseUrl } from './base-url-util';
import { clearSessionStorage } from './session-storage-utils';
import { throwGenericError } from './error-util';
import { HttpRequestHeaders } from '../constants/http-request';

/**
 * Merge params & options and process cancelToken
 * @param params
 * @param options
 * @returns {{params: *, cancelToken}}
 */
const prepareOptions = (params, options) => ({
  params,
  ...options,
});

const axiosRequest = (config) => axios(config)
  .then((response) => response.data)
  .catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log(error.response.data);
      console.log(error.response.status);
      console.log(error.response.headers);

      // Unauthorised Access
      if (error.response.status === 401) {
        clearSessionStorage();
        window.location.reload();
      }

      throwGenericError(error.response);
    } if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      throwGenericError();
    } else if (error.code === 'ERR_CANCELED') {
      // The request was made but cancelled later
      console.log(error.message);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }
    throw error;
  });

/**
 * Generic handling which will return directly data in case of success
 * and will log error in case of exception and then throws the error for custom handling
 *
 * @returns {*}
 */
const request = async (
  requestType,
  url,
  data,
  params,
  options,
  customHeaders = {},
) => {
  if (!url) {
    throw new Error('URL can not be empty');
  }
  const { service, baseURL } = getServiceAndBaseUrl(url);

  const headers = {
    'platform-id': HttpRequestHeaders.DASHBOARD_PLATFORM_ID,
    ...service.headers,
    ...customHeaders,
  };

  const urlParams = new URLSearchParams(params);

  const config = {
    method: requestType,
    url: `${url}`,
    data,
    headers,
    baseURL,
    ...prepareOptions(urlParams, options),
  };

  return axiosRequest(config);
};

const METHOD = {
  GET: 'get',
  PUT: 'put',
  POST: 'post',
  DELETE: 'delete',
  PATCH: 'patch',
};

export default {

  /**
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.get('get/documents')
   *
   * 2. Promise (then & catch) handling
   *    HttpUtil.get('get/documents')
   *    .then((response) => {
   *      //Success Handling
   *      console.log(response);
   *    })
   *    .catch((error) => {
   *      //Custom Failure Handling
   *      console.log(error);
   *    });
   *
   *
   * 3. URL & params
   *    HttpUtil.get('get/document', {id: 1234});
   *
   *
   * 4. URL & Cancel Token Handler
   *    let cancelRequestMethod;
   *    let options = {
   *     cancel: (c) => {
   *        cancelRequestMethod = c;
   *      },
   *    }
   *    HttpUtil.get('/search/documents', null, options);
   *
   *    if (typeof cancelRequestMethod === 'function') {
   *       cancelRequestMethod();
   *    }
   *
   *   pass a method with key cancel in the options which will expect a cancellation method in
   *   parameter that can be used to cancel the request if needed.
   *   to cancel the request simply call the method cancelRequestMethod() as in the example
   *
   *
   * 5. URL, params and options
   *    HttpUtil.get('/search/documents', {id: 123}, options);
   *
   *    options can be from the e.g
   *    {
   *       headers: {'X-Requested-With': 'XMLHttpRequest'},
   *       timeout: 1000,
   *       withCredentials: false,
   *       responseType: 'json',
   *       responseEncoding: 'utf8',
   *       xsrfCookieName: 'XSRF-TOKEN'
   *       xsrfHeaderName: 'X-XSRF-TOKEN',
   *    }
   *    There are more options available, please refer below link for more
   *    @link https://github.com/axios/axios#request-config
   *
   *
   * @param url
   * @param params
   * @param options
   * @param customHeaders
   * @returns {*}
   */
  get(url, params, options, customHeaders) {
    return request(METHOD.GET, url, undefined, params, options, customHeaders);
  },

  /**
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.post('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.post('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.post('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.post('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   * 5. URL, Data, Params, Options and headers
   *    HttpUtil.post('filter/documents', { id: 4567 }, {type: 'document'}, options, headers);
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @param headers
   * @returns {*}
   */
  post(url, data, params, options, headers) {
    return request(METHOD.POST, url, data, params, options, headers);
  },

  /**
   *
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.put('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.put('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.put('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.put('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @returns {*}
   */
  put(url, data, params, options) {
    return request(METHOD.PUT, url, data, params, options);
  },

  /**
   *
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.delete('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.delete('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.delete('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.delete('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @returns {*}
   */
  delete(url, data, params, options) {
    return request(METHOD.DELETE, url, data, params, options);
  },

  patch(url, data, params, options) {
    return request(METHOD.PATCH, url, data, params, options);
  },

  getCrossServer(url) {
    const config = {
      method: METHOD.GET,
      url,
    };
    return axiosRequest(config);
  },
};
